import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { MLML_LEAD_COOKIE_NAME } from 'constants/cookies';
import {
  DEFAULT_AGENT_PROFILE_PIC_PATH,
  isAllowedImageDomain,
} from 'utils/agent-photo-url';

export interface AgentInfo {
  id: string;
  firstName: string;
  lastName: string;
  photo: string;
  email?: string;
  // See https://github.com/zoocasa/zoocasa-next/pull/4356/changes#diff-cb5ec609c67ef1aeaf6c8b5be3af0c0cb75f84967d715b3427550cdeee407befR103
  phoneNumber?: string;
}

const DEFAULT_AVATAR = DEFAULT_AGENT_PROFILE_PIC_PATH;

interface UseMLMLAgentInfoOptions {
  enablePolling?: boolean;
}

interface UseMLMLAgentInfoReturn {
  agentInfo: AgentInfo | null | undefined;
  imgSrc: string;
  useOptimization: boolean;
  handleImageError: () => void;
  hasValidPhoto: boolean;
}

/**
 * Hook to manage MLML (My Link My Lead) agent information from cookies
 * Handles agent data retrieval, image validation, and error handling
 *
 * @param options - Configuration options
 * @param options.enablePolling - If true, polls cookie every 1 second until found (useful for banner)
 * @returns Agent info, image source, optimization flag, and error handler
 */
export function useMLMLAgentInfo(
  options: UseMLMLAgentInfoOptions = {}
): UseMLMLAgentInfoReturn {
  const { enablePolling = false } = options;
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>();
  const [imgSrc, setImgSrc] = useState(DEFAULT_AVATAR);
  const [useOptimization, setUseOptimization] = useState(true);
  const [hasValidPhoto, setHasValidPhoto] = useState(false);

  const handleImageError = () => {
    setImgSrc(DEFAULT_AVATAR);
    setUseOptimization(true);
  };

  useEffect(() => {
    const processAgentInfo = (parsedAgentInfo: AgentInfo) => {
      setAgentInfo(parsedAgentInfo);

      const agentPhoto = parsedAgentInfo?.photo;
      if (agentPhoto && isAllowedImageDomain(agentPhoto)) {
        setImgSrc(agentPhoto);
        setUseOptimization(true);
        setHasValidPhoto(true);
      } else if (agentPhoto) {
        console.warn('Agent photo from unconfigured domain, using default avatar:', agentPhoto);
        setImgSrc(DEFAULT_AVATAR);
        setUseOptimization(true);
        setHasValidPhoto(false);
      } else {
        setImgSrc(DEFAULT_AVATAR);
        setUseOptimization(true);
        setHasValidPhoto(false);
      }
    };

    const checkCookie = (): boolean => {
      const cookieValue = Cookies.get(MLML_LEAD_COOKIE_NAME);
      if (cookieValue) {
        const parsedAgentInfo = JSON.parse(decodeURIComponent(cookieValue));
        processAgentInfo(parsedAgentInfo);
        return true;
      }
      return false;
    };

    if (enablePolling) {
      const checkCookieWithTimeout = () => {
        if (!checkCookie()) {
          setTimeout(checkCookieWithTimeout, 1000);
        }
      };
      checkCookieWithTimeout();
    } else {
      checkCookie();
    }
  }, [enablePolling]);

  return {
    agentInfo,
    imgSrc,
    useOptimization,
    handleImageError,
    hasValidPhoto,
  };
}

